import React from 'react';
import {
  UsersIcon,
  ExclamationTriangleIcon,
  CurrencyPoundIcon,
  CalendarIcon,
  ClockIcon,
  DocumentTextIcon,
  EyeIcon,
  PencilIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  ArrowRightIcon,
  LightBulbIcon,
  ChartBarIcon,
  ScaleIcon,
  UserIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';

export default function StaffDetailsHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-teal-50">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-500 to-teal-600 rounded-full mb-6">
            <UsersIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Records Management Guide</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Comprehensive HR dashboard for monitoring staff performance, compliance, and compensation
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <div className="flex items-start space-x-2 md:space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <LightBulbIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">What is Staff Details?</h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                This is your centralized HR command center - a comprehensive staff management dashboard that provides complete visibility into your workforce. Track attendance patterns, monitor policy compliance, calculate compensation, and manage performance reviews all from one unified interface designed to streamline HR operations and ensure organizational compliance.
              </p>
            </div>
          </div>
        </div>

        {/* Table Overview */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Staff Summary Table Overview</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Basic Information */}
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <UserIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Basic Information</h3>
              <p className="text-sm text-gray-600">Staff names, roles, and employment status</p>
            </div>

            {/* Attendance Tracking */}
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <ClockIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Attendance Tracking</h3>
              <p className="text-sm text-gray-600">Sick days, late occasions, and holiday usage</p>
            </div>

            {/* Compensation */}
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <CurrencyPoundIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Compensation</h3>
              <p className="text-sm text-gray-600">Salary calculations and contract details</p>
            </div>

            {/* Performance */}
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <ChartBarIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Performance</h3>
              <p className="text-sm text-gray-600">Appraisals, probation status, and reviews</p>
            </div>

            {/* Policy Compliance */}
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <ScaleIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Policy Compliance</h3>
              <p className="text-sm text-gray-600">Visual indicators for policy adherence</p>
            </div>

            {/* Actions */}
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <ClipboardDocumentListIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Management Actions</h3>
              <p className="text-sm text-gray-600">Individual details, appraisals, and disciplinary actions</p>
            </div>
          </div>
        </div>

        {/* Attendance & Compliance Tracking */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Attendance & Policy Compliance</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Sick Days Tracking */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <ExclamationTriangleIcon className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Sick Days Monitoring</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Sick Days:</span>
                  <span className="font-semibold text-green-600">Individual count per staff member</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Policy Threshold:</span>
                  <span className="font-semibold text-green-600">Company sick day allowance</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Visual Indicator:</span>
                  <span className="font-semibold text-green-600">Green = Within policy, Red = Exceeds limit</span>
                </div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded p-3 mt-4">
                <p className="text-green-800 text-sm">Click "View Details" to see comprehensive sick leave history and patterns</p>
              </div>
            </div>

            {/* Late Occasions */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <ClockIcon className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Late Arrival Tracking</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Late Occasions:</span>
                  <span className="font-semibold text-green-600">Number of times staff arrived late</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Policy Limit:</span>
                  <span className="font-semibold text-green-600">Maximum allowed late arrivals</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Progress Bar:</span>
                  <span className="font-semibold text-green-600">Visual representation of policy compliance</span>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-4">
                <p className="text-amber-800 text-sm">Red progress bars indicate policy violations requiring disciplinary action</p>
              </div>
            </div>
          </div>
        </div>

        {/* Compensation & Contracts */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Compensation & Contract Management</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Contract Types */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <DocumentTextIcon className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Contract Types</h3>
              </div>
              <div className="space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-blue-800 text-sm"><strong>Standard Contracts:</strong> Fixed hours and days per week</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded p-3">
                  <p className="text-purple-800 text-sm"><strong>Zero Hours:</strong> Variable hours based on actual work performed</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm mt-4">
                The system automatically calculates salaries based on contract type, using either contracted hours or actual average hours worked.
              </p>
            </div>

            {/* Salary Calculations */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CurrencyPoundIcon className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Salary Breakdown</h3>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <p>• <strong>Hourly Rate:</strong> Base pay per hour</p>
                <p>• <strong>Weekly Salary:</strong> Hourly rate × effective hours</p>
                <p>• <strong>Monthly Salary:</strong> Weekly salary × 4.33 (average weeks per month)</p>
                <p>• <strong>Yearly Salary:</strong> Weekly salary × 52 weeks</p>
                <p>• <strong>Effective Hours:</strong> Contracted hours (standard) or average hours (zero hours)</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded p-3 mt-4">
                <p className="text-green-800 text-sm">All calculations are automatically updated based on contract type and actual performance data</p>
              </div>
            </div>
          </div>
        </div>

        {/* Performance & Development */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Performance Management & Development</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <CalendarIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Last Appraisal</h3>
              <p className="text-sm text-gray-600">Date of most recent performance review</p>
            </div>

            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <ScaleIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Probation Status</h3>
              <p className="text-sm text-gray-600">Probation period end date or completion status</p>
            </div>

            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <ClipboardDocumentListIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Management Actions</h3>
              <p className="text-sm text-gray-600">Access individual details, appraisals, and disciplinary actions</p>
            </div>
          </div>
        </div>

        {/* Action Buttons Guide */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Management Actions & Navigation</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-blue-50 rounded-lg border border-blue-200">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <EyeIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">View Individual Details</h3>
              <p className="text-sm text-gray-600">Access comprehensive staff profile with personal information, contact details, and employment history</p>
            </div>

            <div className="text-center p-6 bg-purple-50 rounded-lg border border-purple-200">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <DocumentTextIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">View Appraisals</h3>
              <p className="text-sm text-gray-600">Review performance reviews, development plans, and historical appraisal records</p>
            </div>

            <div className="text-center p-6 bg-amber-50 rounded-lg border border-amber-200">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <PencilIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Edit Appraisal</h3>
              <p className="text-sm text-gray-600">Create new performance reviews, update existing appraisals, or schedule future evaluations</p>
            </div>
          </div>
        </div>

        {/* Holiday Management */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Holiday & Leave Management</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Holiday Tracking */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CalendarIcon className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Holiday Usage</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Days Taken:</span>
                  <span className="font-semibold text-green-600">Actual holiday days used</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Annual Allowance:</span>
                  <span className="font-semibold text-green-600">Total holiday entitlement</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Format:</span>
                  <span className="font-semibold text-green-600">"Taken / Total" (e.g., "15 / 25")</span>
                </div>
              </div>
            </div>

            {/* Holiday Planning */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Holiday Planning</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Monitor holiday usage to ensure adequate coverage and prevent overbooking during peak periods.
              </p>
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <p className="text-green-800 text-sm">Use individual staff details to view detailed holiday calendars and request patterns</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl p-6 md:p-8 border border-green-100">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <LightBulbIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Staff Management Best Practices</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Review the dashboard weekly to identify attendance patterns and potential issues early</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Monitor sick day usage against policy limits to ensure fair and consistent application</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Use the visual progress bars to quickly identify staff requiring disciplinary action</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Regularly review probation status dates to ensure timely probation reviews</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Schedule performance appraisals before they become overdue</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Use individual staff details to understand context behind attendance issues</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Monitor holiday usage to prevent last-minute leave requests during busy periods</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Cross-reference salary calculations with contract types to ensure accuracy</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Document all disciplinary actions and performance issues for legal compliance</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Use the dashboard data to inform workforce planning and recruitment needs</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}